import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, Text, View } from 'react-native';
import Animated, {
	Easing,
	Extrapolation,
	interpolate,
	SharedValue,
	useAnimatedReaction,
	useAnimatedStyle,
	useSharedValue,
	withTiming,
} from 'react-native-reanimated';

import { carouselFocus } from './carouselFocus';
import { TouchableOpacity } from './Touchable';

const DOT_SIZE = 7;
const DOT_ACTIVE_SIZE = 9;
const DOT_GAP = 10;
const DOT_INACTIVE_COLOR = '#FFBF9E';
const DOT_INACTIVE_OPACITY = 0.3;
const DOT_ACTIVE_COLOR = '#FFCC33';
const DOT_GLOW_COLOR = '#FFB81A';
const ROW_GAP = 6;
const CHEVRON_COLOR = '#FFCC59';
const CHEVRON_SIZE = 13;
const LABEL_COLOR = '#FAE0A8';
const LABEL_SIZE = 12;
const HINT_GAP = 12;

const TRANSITION_MS = 220;

// With more cards than dots the row is a window onto the list. The dots at an end with more cards
// past it shrink - the outermost to 3/7 of a dot, the one inside it to 5/7 - and one slot further
// out a dot is gone. Sizes are read off a dot's fractional slot, so a dot sliding out of the window
// shrinks away continuously instead of popping.
const EDGE_SLOTS = [-1, 0, 1, 2];
const EDGE_SCALES = [0, 3 / 7, 5 / 7, 1];
/** Dots mounted past each end of the window, so the one sliding in is already there. */
const MOUNT_MARGIN = 2;

const edgeShrink = (slot: number, start: number, visible: number, count: number) => {
	'worklet';

	// Each end shrinks only as far as there are cards beyond it, so reaching the first or last card
	// grows the end dots back smoothly.
	const before = Math.min(Math.max(start, 0), 1);
	const after = Math.min(Math.max(count - visible - start, 0), 1);
	const left = interpolate(slot, EDGE_SLOTS, EDGE_SCALES, Extrapolation.CLAMP);
	const right = interpolate(visible - 1 - slot, EDGE_SLOTS, EDGE_SCALES, Extrapolation.CLAMP);

	return Math.min(1 - before * (1 - left), 1 - after * (1 - right));
};

/** 1 for a dot inside the window, falling to 0 over the slot past either end. */
const windowPresence = (slot: number, visible: number) => {
	'worklet';

	return interpolate(slot, [-1, 0, visible - 1, visible], [0, 1, 1, 0], Extrapolation.CLAMP);
};

/**
 * A dot's scale: its edge size, except that the lit dot is full size wherever it sits in the
 * window - but only inside it, so a lit dot the window has left behind stays hidden.
 */
const dotScale = (
	index: number,
	start: number,
	lit: number,
	growth: number,
	visible: number,
	count: number,
) => {
	'worklet';

	const slot = index - start;
	const shrink = edgeShrink(slot, start, visible, count);

	return shrink + (growth - shrink) * lit * windowPresence(slot, visible);
};

export type CarouselNavProps = {
	count: number;
	activeIndex: number;
	progress?: SharedValue<number>;
	onDotPress?: (index: number) => void;
	onStep?: (direction: -1 | 1) => void;
	hintLabel?: string;
	scale?: number;
	maxDots?: number;
};

const Dot = ({
	index,
	count,
	value,
	windowStart,
	visibleCount,
	pitch,
	size,
	activeSize,
	onPress,
}: {
	index: number;
	count: number;
	value: SharedValue<number>;
	windowStart: SharedValue<number>;
	visibleCount: number;
	pitch: number;
	size: number;
	activeSize: number;
	onPress?: () => void;
}) => {
	const growth = activeSize / size;

	const slotStyle = useAnimatedStyle(
		() => ({ transform: [{ translateX: (index - windowStart.value) * pitch }] }),
		[index, pitch],
	);

	// Both shared values are read in the style worklets themselves: on web a style only re-runs for
	// the shared values read directly in it, not in a helper it calls.
	const baseStyle = useAnimatedStyle(() => {
		const lit = carouselFocus(value.value, index, count);

		return {
			transform: [{ scale: dotScale(index, windowStart.value, lit, growth, visibleCount, count) }],
		};
	}, [count, growth, index, visibleCount]);

	const activeStyle = useAnimatedStyle(() => {
		const lit = carouselFocus(value.value, index, count);
		const start = windowStart.value;

		return {
			opacity: lit * windowPresence(index - start, visibleCount),
			transform: [{ scale: dotScale(index, start, lit, growth, visibleCount, count) }],
		};
	}, [count, growth, index, visibleCount]);

	const circle = { width: size, height: size, borderRadius: size / 2 };

	return (
		<Animated.View style={[styles.dotSlot, slotStyle]}>
			<TouchableOpacity
				accessibilityRole='button'
				disabled={!onPress}
				onPress={onPress}
				hitSlop={{ top: 10, bottom: 10, left: 6, right: 6 }}
				style={[styles.dotTarget, { width: activeSize, height: activeSize }]}
			>
				<Animated.View style={[styles.dotBase, styles.dotInactive, circle, baseStyle]} />
				<Animated.View
					style={[
						styles.dotBase,
						styles.dotActive,
						glowStyle(activeSize / DOT_ACTIVE_SIZE),
						circle,
						activeStyle,
					]}
				/>
			</TouchableOpacity>
		</Animated.View>
	);
};

const Chevron = ({
	glyph,
	size,
	label,
	onPress,
}: {
	glyph: string;
	size: number;
	label: string;
	onPress?: () => void;
}) => {
	const text = <Text style={[styles.chevron, { fontSize: size }]}>{glyph}</Text>;

	if (!onPress) return text;

	return (
		<TouchableOpacity
			accessibilityRole='button'
			accessibilityLabel={label}
			onPress={onPress}
			hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
		>
			{text}
		</TouchableOpacity>
	);
};

export const CarouselNav = ({
	count,
	activeIndex,
	progress,
	onDotPress,
	onStep,
	hintLabel,
	scale = 1,
	maxDots = 7,
}: CarouselNavProps) => {
	const { t } = useTranslation();
	const dotSize = DOT_SIZE * scale;
	const dotActiveSize = DOT_ACTIVE_SIZE * scale;
	const gap = DOT_GAP * scale;
	const pitch = dotActiveSize + gap;

	const eased = useSharedValue(activeIndex);
	const value = progress ?? eased;

	useEffect(() => {
		if (progress || count <= 0) return;

		const turns = Math.round((eased.value - activeIndex) / count);

		eased.value = withTiming(activeIndex + turns * count, {
			duration: TRANSITION_MS,
			easing: Easing.out(Easing.quad),
		});
	}, [activeIndex, count, eased, progress]);

	const visibleCount = Math.min(count, maxDots);
	const lastStart = Math.max(0, count - visibleCount);

	// The first card the window shows, fractional while it slides. It follows the carousel's own
	// position frame by frame rather than the settled index: the lit dot walks along the row and,
	// once it is one dot from either end, pushes the window along with it, so it never runs into the
	// shrunken end dot and the row never jumps when a swipe settles.
	const initialStart = Math.max(0, Math.min(activeIndex - Math.floor(visibleCount / 2), lastStart));
	const windowStart = useSharedValue(initialStart);
	const windowTarget = useSharedValue(initialStart);

	useAnimatedReaction(
		() => value.value,
		raw => {
			let target = windowTarget.value;
			let settled = false;

			if (count > visibleCount) {
				// Halfway between the last card and the first counts as the first, so a wrap moves the
				// window over while the light is split between the two, never once it has faded out.
				let position = ((raw % count) + count) % count;
				if (position > count - 0.5) position -= count;
				if (position > target + visibleCount - 2) target = position - (visibleCount - 2);
				else if (position < target + 1) target = position - 1;
				// A drag that pushed the window part of a dot and then sprang back leaves it between
				// dots, with the end dots half shrunk. Once the carousel rests on a card, round it to a
				// whole dot - which always keeps the lit dot off both ends.
				settled = Math.abs(position - Math.round(position)) < 0.01;
				if (settled) target = Math.round(target);
			}
			target = Math.min(Math.max(target, 0), lastStart);

			if (target === windowTarget.value) return;

			const rounding = settled && Math.abs(target - windowTarget.value) < 1;
			windowTarget.value = target;
			// The wrap from the last card back to the first moves the window the whole list in one
			// step; it switches over at the moment the card does rather than sliding past every dot.
			windowStart.value = rounding
				? withTiming(target, { duration: TRANSITION_MS, easing: Easing.out(Easing.quad) })
				: target;
		},
		[count, lastStart, visibleCount],
	);

	// Mount the dots any window holding the active card can show. Near either end of a long list the
	// carousel can wrap, so the window at the other end is mounted too and is ready when it lands.
	const reach = visibleCount + MOUNT_MARGIN;
	const mounted: number[] = [];
	for (let index = 0; index < count; index++) {
		const near = Math.abs(index - activeIndex) <= reach;
		const wrapTarget =
			(activeIndex >= count - reach && index < reach) ||
			(activeIndex < reach && index >= count - reach);
		if (near || wrapTarget) mounted.push(index);
	}

	const handlePress = useCallback((index: number) => () => onDotPress?.(index), [onDotPress]);
	const handlePrev = useCallback(() => onStep?.(-1), [onStep]);
	const handleNext = useCallback(() => onStep?.(1), [onStep]);

	if (count <= 0) return null;

	return (
		<View style={styles.root} pointerEvents='box-none'>
			<View style={{ width: visibleCount * pitch - gap, height: dotActiveSize }}>
				{mounted.map(index => (
					<Dot
						key={index}
						index={index}
						count={count}
						value={value}
						windowStart={windowStart}
						visibleCount={visibleCount}
						pitch={pitch}
						size={dotSize}
						activeSize={dotActiveSize}
						onPress={onDotPress ? handlePress(index) : undefined}
					/>
				))}
			</View>

			{hintLabel ? (
				<View style={[styles.hintRow, { marginTop: ROW_GAP * scale, columnGap: HINT_GAP * scale }]}>
					<Chevron
						glyph={'«'}
						size={CHEVRON_SIZE * scale}
						label={t('T_SITE_A11Y_PREVIOUS_ROOM', { defaultValue: 'Previous room' })}
						onPress={onStep ? handlePrev : undefined}
					/>
					<Text style={[styles.label, { fontSize: LABEL_SIZE * scale }]}>{hintLabel}</Text>
					<Chevron
						glyph={'»'}
						size={CHEVRON_SIZE * scale}
						label={t('T_SITE_A11Y_NEXT_ROOM', { defaultValue: 'Next room' })}
						onPress={onStep ? handleNext : undefined}
					/>
				</View>
			) : null}
		</View>
	);
};

const glowStyle = (glowScale: number) =>
	Platform.select({
		android: undefined,
		default: {
			shadowColor: DOT_GLOW_COLOR,
			shadowOpacity: 0.9,
			shadowRadius: 6 * glowScale,
			shadowOffset: { width: 0, height: 0 },
		},
	});

const styles = StyleSheet.create({
	root: {
		alignItems: 'center',
		justifyContent: 'center',
	},
	dotSlot: {
		position: 'absolute',
		left: 0,
		top: 0,
	},
	dotTarget: {
		alignItems: 'center',
		justifyContent: 'center',
	},
	dotBase: {
		position: 'absolute',
	},
	dotInactive: {
		backgroundColor: DOT_INACTIVE_COLOR,
		opacity: DOT_INACTIVE_OPACITY,
	},
	dotActive: {
		backgroundColor: DOT_ACTIVE_COLOR,
	},
	hintRow: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
	},
	chevron: {
		color: CHEVRON_COLOR,
		fontFamily: 'Sofia Sans',
		fontWeight: '700',
	},
	label: {
		color: LABEL_COLOR,
		fontFamily: 'Sofia Sans',
		fontWeight: '600',
		letterSpacing: 0.12,
	},
});

export default CarouselNav;
